import { storeToRefs } from 'pinia';
import { useRootStore, usePermissions } from '@qsrsoft/myq-core';

/**
 * @param {string[]} permissions Permissions required to access the route
 * @param {string[]} appPermissions More permissions required to access the route, but these are for apps, so we check if the org has the required apps enabled
 * @param {string} fallback Fallback route
 */
function addPermissionsToRoute(
  permissions = [],
  appPermissions = [],
  fallback = '/',
) {
  return {
    async beforeEnter(_to, _from, next) {
      const store = useRootStore();
      await store.waitForUserToBeLoaded();

      // Check if the user has the required permissions
      const { hasPermission } = usePermissions([
        'training_access',
        ...permissions,
      ]);

      // Check if the org has the required apps enabled
      const { orgApps } = storeToRefs(store);
      const allAppsIncluded = ['myq_ops', ...appPermissions].every((app) =>
        orgApps.value.includes(app),
      );
      // Check permissions and app access for all environments
      if (hasPermission.value && allAppsIncluded) {
        next();
      } else {
        // Redirect to the fallback URL with access_denied parameter
        // This can be used to display an error message
        const redirectUrl = new URL(fallback, window.location.origin);
        redirectUrl.searchParams.set('access_denied', 'true');
        redirectUrl.searchParams.set(
          'required_permission',
          Array.isArray(permissions) ? permissions.join(',') : permissions,
        );
        window.location.href = redirectUrl.toString();
      }
    },
    permissions: typeof permissions === 'string' ? [permissions] : permissions,
  };
}

export const trainingRoutes = [
  {
    path: '/training',
    name: 'Training',
    redirect: '/training/records/dashboard',
    children: [
      {
        path: '/training/courses',
        name: 'Courses',
        meta: {
          id: 'training-manager',
          disableLocationSelector: true,
        },
        component: () => import('@/projects/training/pages/manager.vue'),
        ...addPermissionsToRoute(),
      },
      {
        path: '/training/records/dashboard',
        name: 'Records',
        meta: {
          id: 'training-records',
        },
        component: () => import('@/projects/training/pages/cert_in_row.vue'),
        ...addPermissionsToRoute(),
      },
      {
        path: '/training/classes',
        name: 'Classes',
        meta: {
          id: 'training-classes',
        },
        component: () =>
          import('@/projects/training/pages/calendar/myEvents/MyEvents.vue'),
        ...addPermissionsToRoute(),
      },
      {
        path: '/training/print',
        name: 'Training Print',
        meta: {
          id: 'print',
        },
        component: () =>
          import('@/projects/training/pages/orientationLetter.vue'),
        ...addPermissionsToRoute(),
      },
    ],
  },
];
